import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Shapes, Plus, Search, Edit2, Trash2 } from 'lucide-react';
import axios from 'axios';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import Table from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface Type {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

const TypesPage: React.FC = () => {
  const [types, setTypes] = useState<Type[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<Type | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchTypes = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/types`, axiosConfig);
      setTypes(response.data.types || []);
    } catch (error) {
      toast.error('Failed to fetch types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const handleOpenModal = (type?: Type) => {
    if (type) {
      setEditingType(type);
      setFormData({ name: type.name, description: type.description || '' });
    } else {
      setEditingType(null);
      setFormData({ name: '', description: '' });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingType(null);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Type name is required');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      if (editingType) {
        await axios.put(`${API_URL}/types/${editingType._id}`, formData, axiosConfig);
        toast.success('Type updated successfully');
      } else {
        await axios.post(`${API_URL}/types`, formData, axiosConfig);
        toast.success('Type created successfully');
      }
      handleCloseModal();
      fetchTypes();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this type?')) return;

    try {
      const token = localStorage.getItem('token');
      const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_URL}/types/${id}`, axiosConfig);
      toast.success('Type deleted successfully');
      fetchTypes();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete type');
    }
  };

  const filteredTypes = types.filter(type =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { header: 'Name', accessor: (row: any) => row.name },
    { header: 'Description', accessor: (row: Type) => row.description || '-' },
    {
      header: 'Actions',
      accessor: (row: Type) => (
        <div className="flex gap-0.5">
          <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" icon={<Edit2 className="w-4 h-4" />} onClick={() => handleOpenModal(row)} />
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" icon={<Trash2 className="w-4 h-4" />} onClick={() => handleDelete(row._id)} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Types</h1>
          <p className="mt-2 text-sm text-gray-600">Manage type information</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Type</Button>
      </motion.div>

      <Card padding="md">
        <Input placeholder="Search types..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} icon={<Search className="w-5 h-5" />} fullWidth />
      </Card>

      <Card padding="none">
        {loading ? (
          <div className="flex justify-center items-center py-12"><LoadingSpinner size="lg" /></div>
        ) : filteredTypes.length > 0 ? (
          <Table data={filteredTypes} columns={columns} emptyMessage="No types found" />
        ) : (
          <div className="px-6 py-12 text-center">
            <Shapes className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No types yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first type.</p>
            <div className="mt-6"><Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add Type</Button></div>
          </div>
        )}
      </Card>

      <Modal isOpen={modalOpen} onClose={handleCloseModal} title={editingType ? 'Edit Type' : 'Add New Type'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter type name" required fullWidth />
          <Input label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Enter description (optional)" fullWidth />
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={submitting}>Cancel</Button>
            <Button type="submit" loading={submitting}>{editingType ? 'Update Type' : 'Create Type'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default TypesPage;

